import { useEffect, useMemo, useState } from "react";
import PaginationControls from "../../../components/base/PaginationControls";
import { useDarkMode } from "../../../context/ThemeContext";
import { brand } from "../../../config/theme";
import Swal from "sweetalert2";
import type { TruckYardUserType } from "../../../store/truckYardStore";
import { useTruckYardStore } from "../../../store/truckYardStore";
import HeaderComponent from "../components/HeaderComponent";
import {
  NO_YARD_FILTER,
  matchesYardSelection,
  pruneYardSelection,
} from "../components/yardMultiFilter";
import NoAssignedYardsNotice from "../../../components/base/NoAssignedYardsNotice";
import useTruckYardRole from "../../../hooks/useTruckYardRole";
import { formatScheduleDate } from "../../../utils/getDate";

// ─── Types ───────────────────────────────────────────────────────────────────

interface DaySchedule {
  enabled: boolean;
  from: string;
  to: string;
}

interface WeekSchedule {
  monday: DaySchedule;
  tuesday: DaySchedule;
  wednesday: DaySchedule;
  thursday: DaySchedule;
  friday: DaySchedule;
  saturday: DaySchedule;
  sunday: DaySchedule;
}

interface Person {
  id: string;
  type: TruckYardUserType;
  firstName: string;
  lastName: string;
  username: string;
  phone: string;
  email: string;
  status: "active" | "inactive";
  /**
   * Los patios de la persona, siempre en plural (issue #497). Un vigilante de
   * un patio es `["x"]`, no un caso aparte: el escalar `yard_id` del esquema
   * viejo lo absorbe normalizeUser.
   */
  yard_ids: string[];
  schedule: WeekSchedule;
  /**
   * Si está apagado, la sesión de esta persona deja de validarse contra la
   * geocerca del patio (utils/accessValidation.ts). El horario se sigue
   * validando. Solo aplica a vigilantes, supervisores e inspectores: los
   * admins nunca pasan por esa validación.
   */
  geofence_validation_enabled: boolean;
  identification_url?: string;
  createdAt: string;
  updatedAt: string;
}

interface PersonFormData {
  type: TruckYardUserType;
  firstName: string;
  lastName: string;
  username: string;
  phone: string;
  email: string;
  password: string;
  confirmPassword: string;
  status: "active" | "inactive";
  yard_ids: string[];
  schedule: WeekSchedule;
  geofence_validation_enabled: boolean;
  identification_url: string;
  identificationFile: File | null;
}

interface ThemeClasses {
  bg: string;
  border: string;
  text: string;
  textSecondary: string;
  cardBg: string;
  cardBorder: string;
  buttonHover: string;
  buttonText: string;
  buttonTextHover: string;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const DEFAULT_SCHEDULE: WeekSchedule = {
  monday: { enabled: true, from: "06:00", to: "18:00" },
  tuesday: { enabled: true, from: "06:00", to: "18:00" },
  wednesday: { enabled: true, from: "06:00", to: "18:00" },
  thursday: { enabled: true, from: "06:00", to: "18:00" },
  friday: { enabled: true, from: "06:00", to: "18:00" },
  saturday: { enabled: false, from: "", to: "" },
  sunday: { enabled: false, from: "", to: "" },
};

const EMPTY_FORM: PersonFormData = {
  type: "guards",
  firstName: "",
  lastName: "",
  username: "",
  phone: "",
  email: "",
  password: "",
  confirmPassword: "",
  status: "active",
  yard_ids: [],
  schedule: DEFAULT_SCHEDULE,
  geofence_validation_enabled: true,
  identification_url: "",
  identificationFile: null,
};

const HOURS = Array.from(
  { length: 24 },
  (_, i) => `${String(i).padStart(2, "0")}:00`,
);

const DAYS: { key: keyof WeekSchedule; label: string }[] = [
  { key: "monday", label: "Lunes" },
  { key: "tuesday", label: "Martes" },
  { key: "wednesday", label: "Miércoles" },
  { key: "thursday", label: "Jueves" },
  { key: "friday", label: "Viernes" },
  { key: "saturday", label: "Sábado" },
  { key: "sunday", label: "Domingo" },
];

const PAGE_SIZE = 10;

type TypeMeta = {
  singular: string;
  plural: string;
  addLabel: string;
  badgeBg: string;
  badgeText: string;
  avatarBg: string;
  avatarText: string;
  icon: string;
};

const TYPE_META: Record<TruckYardUserType, TypeMeta> = {
  guards: {
    singular: "Vigilante",
    plural: "Vigilantes",
    addLabel: "Vigilante",
    badgeBg: "bg-blue-500/15",
    badgeText: "text-blue-400",
    avatarBg: "bg-blue-500/20",
    avatarText: "text-blue-400",
    icon: "ri-shield-user-line",
  },
  supervisors: {
    singular: "Supervisor",
    plural: "Supervisores",
    addLabel: "Supervisor",
    badgeBg: "bg-purple-500/15",
    badgeText: "text-purple-400",
    avatarBg: "bg-purple-500/20",
    avatarText: "text-purple-400",
    icon: "ri-user-star-line",
  },
  inspectors: {
    singular: "Inspector",
    plural: "Inspectores",
    addLabel: "Inspector",
    badgeBg: "bg-teal-500/15",
    badgeText: "text-teal-400",
    avatarBg: "bg-teal-500/20",
    avatarText: "text-teal-400",
    icon: "ri-search-eye-line",
  },
  admins: {
    singular: "Administrador",
    plural: "Administradores",
    addLabel: "Administrador",
    badgeBg: "bg-amber-500/15",
    badgeText: "text-amber-400",
    avatarBg: "bg-amber-500/20",
    avatarText: "text-amber-400",
    icon: "ri-admin-line",
  },
  // Admin de patios (#558). Se nombra distinto del "Administrador" a propósito:
  // la confusión entre los dos es lo que dejó que 5 usuarios de producción
  // terminaran con un rol que el código no conocía.
  yardAdmins: {
    singular: "Admin de Patios",
    plural: "Admins de Patios",
    addLabel: "Admin de Patios",
    badgeBg: "bg-orange-500/15",
    badgeText: "text-orange-400",
    avatarBg: "bg-orange-500/20",
    avatarText: "text-orange-400",
    icon: "ri-building-line",
  },
};

const TYPE_ORDER: TruckYardUserType[] = [
  "guards",
  "supervisors",
  "inspectors",
  "yardAdmins",
  "admins",
];

/**
 * Qué tipos de personal admiten más de un patio.
 *
 * Es una REGLA declarada, no una consecuencia del esquema: desde el issue #497
 * los cuatro tipos guardan `yard_ids: string[]`, así que nada en los datos
 * impide darle dos patios a un vigilante — lo impide esta regla, y su gemela
 * del backend (`SINGLE_YARD_TYPES` en truck-yard/yardAssignment.js), que es la
 * que manda: acá sólo decide si se dibujan checkboxes o un <select>.
 *
 *  - supervisor, admin y admin de patios: 1..N. El supervisor supervisa varios
 *    patios; es literalmente el pedido de operaciones que abrió #497. Para el
 *    admin de patios está medido: cuando el rol tuvo usuarios en producción,
 *    dos tenían 7 patios y tres tenían 1 (#561).
 *  - vigilante: 1. Está parado en la caseta de UN patio y su sesión se valida
 *    contra la geocerca de ese patio (utils/accessValidation.ts toma
 *    `yard_ids[0]`); con dos, validaría contra uno arbitrario.
 *  - inspector: 1. Se dieron de alta así, uno por patio, y comparte con el
 *    vigilante la validación por geocerca.
 */
const MULTI_YARD_TYPES: TruckYardUserType[] = [
  "admins",
  "supervisors",
  "yardAdmins",
];
const allowsMultipleYards = (type: TruckYardUserType) =>
  MULTI_YARD_TYPES.includes(type);

/**
 * Quién puede quedarse sin ningún patio. Sólo el admin de COMPAÑÍA, y espeja
 * `ALLOWS_NO_YARDS` del backend.
 *
 * Para supervisor, inspector y vigilante la lista decide qué ven, y el mínimo
 * de 1 es la barrera contra el fallo abierto de #497. Para el admin no decide
 * nada — `hasYardRestrictions()` ya no lo recorta —, y exigirle uno rompería
 * la edición de los 3 admins de PTAR que en producción no tienen ninguno
 * (general1/2/3.acuapue@dumaxst.com, ACTIVOS): no se podría guardar ni un
 * cambio de teléfono sin inventarles una asignación que no significa nada.
 *
 * El admin de PATIOS (`yardAdmins`) NO está, aunque el nombre se parezca: su
 * lista SÍ decide qué ve (#560 lo mete en `hasYardRestrictions()`), así que
 * cero patios lo deja con todas las pantallas vacías. Acá se rechaza antes de
 * mandar el request, para que el usuario vea el motivo en el formulario y no un
 * 400 `yardIdsRequired` del backend.
 */
const ALLOWS_NO_YARDS: TruckYardUserType[] = ["admins"];
const allowsNoYards = (type: TruckYardUserType) =>
  ALLOWS_NO_YARDS.includes(type);

/**
 * Quién trabaja desde una oficina y por eso no se valida contra la geocerca del
 * patio (`utils/accessValidation.ts`, `useAccessValidation`).
 *
 * Los dos tipos de admin. Para el de patios es una decisión explícita de #560 y
 * no un descuido: comparte lista de apps con el supervisor, y modelarlo sobre
 * él le habría traído la revalidación cada 5 minutos que le cierra la sesión
 * fuera de la geocerca. Un administrador no está parado en la caseta.
 */
const OFFICE_TYPES: TruckYardUserType[] = ["admins", "yardAdmins"];
const usesGeofenceValidation = (type: TruckYardUserType) =>
  !OFFICE_TYPES.includes(type);

// ─── Helpers ─────────────────────────────────────────────────────────────────

const normalizeUser = (user: any, type: TruckYardUserType): Person => ({
  id: user.id,
  type,
  firstName: user.firstName || user.first_name || "",
  lastName: user.lastName || user.last_name || "",
  username: user.username || "",
  phone: user.phone || "",
  email: user.email || "",
  status: user.status || user.status_id || "active",
  // El backend ya devuelve `yard_ids` normalizado, pero el escalar sigue
  // aceptándose por si la respuesta viene de una función todavía sin desplegar.
  yard_ids: Array.isArray(user.yard_ids)
    ? user.yard_ids.filter(Boolean)
    : user.yard_id
      ? [user.yard_id]
      : [],
  schedule: user.schedule || DEFAULT_SCHEDULE,
  // Sin el campo se asume activa: los usuarios dados de alta antes del toggle
  // deben seguir validándose contra la geocerca.
  geofence_validation_enabled: user.geofence_validation_enabled !== false,
  identification_url: user.identification_url || "",
  createdAt: typeof user.createdAt === "string" ? user.createdAt : "-",
  updatedAt: typeof user.updatedAt === "string" ? user.updatedAt : "-",
});

function getInitials(person: Person) {
  const first = person.firstName?.[0] || "";
  const last = person.lastName?.[0] || "";
  return `${first}${last}`.toUpperCase() || "??";
}

function getActiveDays(schedule: WeekSchedule) {
  return DAYS.filter((d) => schedule[d.key]?.enabled)
    .map((d) => d.label.slice(0, 3))
    .join(", ");
}

// ─── Grid Card ───────────────────────────────────────────────────────────────

function PersonGridCard({
  person,
  themeClasses,
  isDarkMode,
  onDetails,
  onEdit,
  onDelete,
}: {
  person: Person;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onDetails: (p: Person) => void;
  onEdit: (p: Person) => void;
  onDelete: (p: Person) => void;
}) {
  const meta = TYPE_META[person.type];
  const surface = isDarkMode ? "bg-[#252525]" : "bg-gray-50";

  return (
    <div
      className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-2xl p-5 hover:shadow-lg transition-all duration-200`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div
            className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${meta.avatarBg} ${meta.avatarText} font-bold text-lg`}
          >
            {getInitials(person)}
          </div>
          <div className="min-w-0">
            <p
              className={`font-semibold text-sm ${themeClasses.text} truncate`}
            >
              {person.firstName} {person.lastName}
            </p>
            <span className={`text-xs font-mono ${themeClasses.textSecondary}`}>
              {person.id}
            </span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <span
            className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${meta.badgeBg} ${meta.badgeText} inline-flex items-center gap-1`}
          >
            <i className={`${meta.icon} text-[10px]`}></i>
            {meta.singular}
          </span>
          <span
            className={`text-xs px-2 py-1 rounded-full font-medium ${
              person.status === "active"
                ? "bg-green-500/15 text-green-400"
                : "bg-red-500/15 text-red-400"
            }`}
          >
            {person.status === "active" ? "Activo" : "Inactivo"}
          </span>
        </div>
      </div>

      <div className={`${surface} rounded-xl p-3 space-y-2 mb-4`}>
        <div className="flex items-center gap-2">
          <i
            className={`ri-mail-line text-xs ${themeClasses.textSecondary}`}
          ></i>
          <span className={`text-xs ${themeClasses.textSecondary} truncate`}>
            {person.email}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <i
            className={`ri-phone-line text-xs ${themeClasses.textSecondary}`}
          ></i>
          <span className={`text-xs ${themeClasses.textSecondary}`}>
            {person.phone}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <i
            className={`ri-calendar-check-line text-xs ${themeClasses.textSecondary}`}
          ></i>
          <span className={`text-xs ${themeClasses.textSecondary} truncate`}>
            {getActiveDays(person.schedule)}
          </span>
        </div>
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => onDetails(person)}
          className={`p-1.5 ${themeClasses.buttonHover} rounded-lg transition-colors`}
          title="Ver detalles"
        >
          <i className={`ri-eye-line text-sm ${themeClasses.buttonText}`}></i>
        </button>
        <button
          onClick={() => onEdit(person)}
          className={`p-1.5 ${themeClasses.buttonHover} rounded-lg transition-colors`}
          title="Editar"
        >
          <i
            className={`ri-pencil-line text-sm ${themeClasses.buttonText}`}
          ></i>
        </button>
        <button
          onClick={() => onDelete(person)}
          className="p-1.5 bg-red-500/10 text-red-500 rounded-lg transition-colors hover:bg-red-500/20"
          title="Eliminar"
        >
          <i className="ri-delete-bin-line text-sm"></i>
        </button>
      </div>
    </div>
  );
}

// ─── List Row ────────────────────────────────────────────────────────────────

function PersonListRow({
  person,
  themeClasses,
  onDetails,
  onEdit,
  onDelete,
}: {
  person: Person;
  themeClasses: ThemeClasses;
  onDetails: (p: Person) => void;
  onEdit: (p: Person) => void;
  onDelete: (p: Person) => void;
}) {
  const meta = TYPE_META[person.type];

  return (
    <div
      className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 hover:shadow-md transition-all duration-200`}
    >
      {/* Mobile */}
      <div className="flex items-center gap-3 sm:hidden">
        <div
          className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${meta.avatarBg} ${meta.avatarText} font-bold text-sm`}
        >
          {getInitials(person)}
        </div>
        <div className="flex-1 min-w-0">
          <p className={`font-semibold text-sm ${themeClasses.text} truncate`}>
            {person.firstName} {person.lastName}
          </p>
          <p className={`text-xs ${themeClasses.textSecondary} truncate`}>
            {person.email}
          </p>
        </div>
        <span
          className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${meta.badgeBg} ${meta.badgeText}`}
        >
          {meta.singular}
        </span>
      </div>

      {/* Desktop */}
      <div className="hidden sm:flex items-center gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div
            className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${meta.avatarBg} ${meta.avatarText} font-bold text-sm`}
          >
            {getInitials(person)}
          </div>
          <div className="min-w-0">
            <p
              className={`font-semibold text-sm ${themeClasses.text} truncate`}
            >
              {person.firstName} {person.lastName}
            </p>
            <span className={`text-xs font-mono ${themeClasses.textSecondary}`}>
              {person.id}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-6 flex-shrink-0">
          <span
            className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${meta.badgeBg} ${meta.badgeText} inline-flex items-center gap-1`}
          >
            <i className={`${meta.icon} text-[10px]`}></i>
            {meta.singular}
          </span>
          <div className="hidden md:block text-center min-w-[160px]">
            <p
              className={`text-[10px] uppercase tracking-wide ${themeClasses.textSecondary}`}
            >
              Correo
            </p>
            <p
              className={`text-sm ${themeClasses.text} truncate max-w-[160px]`}
            >
              {person.email}
            </p>
          </div>
          <div className="hidden lg:block text-center min-w-[130px]">
            <p
              className={`text-[10px] uppercase tracking-wide ${themeClasses.textSecondary}`}
            >
              Teléfono
            </p>
            <p className={`text-sm ${themeClasses.text}`}>{person.phone}</p>
          </div>

          <span
            className={`text-xs px-2.5 py-1 rounded-full font-medium ${
              person.status === "active"
                ? "bg-green-500/15 text-green-400"
                : "bg-red-500/15 text-red-400"
            }`}
          >
            {person.status === "active" ? "Activo" : "Inactivo"}
          </span>

          <div className="flex items-center gap-1">
            <button
              onClick={() => onEdit(person)}
              className={`p-1.5 ${themeClasses.buttonHover} rounded-lg transition-colors`}
              title="Editar"
            >
              <i
                className={`ri-pencil-line text-sm ${themeClasses.buttonText}`}
              ></i>
            </button>
            <button
              onClick={() => onDelete(person)}
              className="p-1.5 bg-red-500/10 text-red-500 rounded-lg transition-colors hover:bg-red-500/20"
              title="Eliminar"
            >
              <i className="ri-delete-bin-line text-sm"></i>
            </button>
            <button
              onClick={() => onDetails(person)}
              className={`p-1.5 ${themeClasses.buttonHover} rounded-lg transition-colors`}
              title="Ver detalles"
            >
              <i
                className={`ri-eye-line text-sm ${themeClasses.buttonText}`}
              ></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Details Modal ───────────────────────────────────────────────────────────

function PersonDetailsModal({
  person,
  yards,
  themeClasses,
  isDarkMode,
  isDeleting,
  onClose,
  onEdit,
  onDelete,
}: {
  person: Person;
  yards: { id: string; name: string }[];
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  isDeleting: boolean;
  onClose: () => void;
  onEdit: (p: Person) => void;
  onDelete: (p: Person) => void;
}) {
  const meta = TYPE_META[person.type];
  const surface = isDarkMode ? "bg-[#252525]" : "bg-gray-50";
  const labelColor = isDarkMode ? "text-gray-400" : "text-gray-500";

  const yardNames = useMemo(
    () =>
      person.yard_ids
        .map((id) => yards.find((y) => y.id === id)?.name || id)
        .filter(Boolean),
    [person.yard_ids, yards],
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className={`relative ${themeClasses.cardBg} rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto`}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-5 border-b border-gray-700/30 bg-inherit rounded-t-2xl">
          <h2 className={`text-base font-bold ${themeClasses.text}`}>
            Detalles del {meta.singular}
          </h2>
          <button
            onClick={onClose}
            className={`p-1.5 ${themeClasses.buttonHover} rounded-lg transition-colors`}
          >
            <i className={`ri-close-line text-xl ${themeClasses.text}`}></i>
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div
                className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${meta.avatarBg} ${meta.avatarText} font-bold text-lg`}
              >
                {getInitials(person)}
              </div>
              <div>
                <h3 className={`text-base font-bold ${themeClasses.text}`}>
                  {person.firstName} {person.lastName}
                </h3>
                <span
                  className={`text-xs font-mono ${themeClasses.textSecondary}`}
                >
                  {person.id}
                </span>
                <div className="mt-1">
                  <span
                    className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${meta.badgeBg} ${meta.badgeText} inline-flex items-center gap-1`}
                  >
                    <i className={`${meta.icon} text-[10px]`}></i>
                    {meta.singular}
                  </span>
                </div>
              </div>
            </div>

            <span
              className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-medium ${
                person.status === "active"
                  ? "bg-green-500/15 text-green-400 border border-green-500/30"
                  : "bg-red-500/15 text-red-400 border border-red-500/30"
              }`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${
                  person.status === "active" ? "bg-green-400" : "bg-red-400"
                }`}
              />
              {person.status === "active" ? "Activo" : "Inactivo"}
            </span>
          </div>

          <div className="space-y-3">
            {person.identification_url && (
              <div className={`${surface} rounded-xl p-4`}>
                <p
                  className={`text-[11px] uppercase tracking-wider ${labelColor} font-medium mb-3`}
                >
                  Identificación
                </p>
                <div className="flex justify-center">
                  <img
                    src={person.identification_url}
                    alt="Identificación"
                    className="max-w-full h-auto rounded-lg border border-gray-700/30"
                    style={{ maxHeight: "200px" }}
                  />
                </div>
              </div>
            )}

            <div className={`${surface} rounded-xl p-4 grid grid-cols-2 gap-4`}>
              <div>
                <p
                  className={`text-[11px] uppercase tracking-wider ${labelColor} font-medium mb-1`}
                >
                  Usuario
                </p>
                <p className={`text-sm ${themeClasses.text} break-all`}>
                  {person.username}
                </p>
              </div>
              <div>
                <p
                  className={`text-[11px] uppercase tracking-wider ${labelColor} font-medium mb-1`}
                >
                  Correo
                </p>
                <p className={`text-sm ${themeClasses.text} break-all`}>
                  {person.email}
                </p>
              </div>
              <div>
                <p
                  className={`text-[11px] uppercase tracking-wider ${labelColor} font-medium mb-1`}
                >
                  Teléfono
                </p>
                <p className={`text-sm ${themeClasses.text}`}>{person.phone}</p>
              </div>
              <div>
                <p
                  className={`text-[11px] uppercase tracking-wider ${labelColor} font-medium mb-1`}
                >
                  {allowsMultipleYards(person.type)
                    ? "Patios asignados"
                    : "Patio"}
                </p>
                <p className={`text-sm ${themeClasses.text}`}>
                  {yardNames.length > 0 ? yardNames.join(", ") : "—"}
                </p>
              </div>
              {usesGeofenceValidation(person.type) && (
                <div>
                  <p
                    className={`text-[11px] uppercase tracking-wider ${labelColor} font-medium mb-1`}
                  >
                    Validación por geocerca
                  </p>
                  <span
                    className={`inline-flex items-center gap-1.5 text-sm font-medium ${
                      person.geofence_validation_enabled
                        ? "text-green-400"
                        : "text-amber-400"
                    }`}
                  >
                    <i
                      className={
                        person.geofence_validation_enabled
                          ? "ri-map-pin-line"
                          : "ri-map-pin-off-line"
                      }
                    />
                    {person.geofence_validation_enabled
                      ? "Activa"
                      : "Desactivada"}
                  </span>
                </div>
              )}
              <div>
                <p
                  className={`text-[11px] uppercase tracking-wider ${labelColor} font-medium mb-1`}
                >
                  Creado
                </p>
                <p className={`text-sm ${themeClasses.text}`}>
                  {formatScheduleDate(person.createdAt)}
                </p>
              </div>
              <div>
                <p
                  className={`text-[11px] uppercase tracking-wider ${labelColor} font-medium mb-1`}
                >
                  Actualizado
                </p>
                <p className={`text-sm ${themeClasses.text}`}>
                  {formatScheduleDate(person.updatedAt)}
                </p>
              </div>
            </div>

            <div className={`${surface} rounded-xl p-4`}>
              <p
                className={`text-[11px] uppercase tracking-wider ${labelColor} font-medium mb-3`}
              >
                Horario de trabajo
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-1">
                {DAYS.map((d) => {
                  const day = person.schedule[d.key];
                  return (
                    <div key={d.key} className="text-center">
                      <p
                        className={`text-[10px] font-semibold ${
                          day?.enabled
                            ? "text-green-400"
                            : themeClasses.textSecondary
                        } mb-1`}
                      >
                        {d.label.slice(0, 3)}
                      </p>
                      {day?.enabled ? (
                        <>
                          <p className={`text-[10px] ${themeClasses.text}`}>
                            {day?.from}
                          </p>
                          <p
                            className={`text-[10px] ${themeClasses.textSecondary}`}
                          >
                            {day?.to}
                          </p>
                        </>
                      ) : (
                        <p
                          className={`text-[10px] ${themeClasses.textSecondary}`}
                        >
                          —
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-700/30 bg-inherit rounded-b-2xl">
          <button
            onClick={() => onEdit(person)}
            className={`px-4 py-2.5 rounded-xl ${themeClasses.buttonHover} border ${themeClasses.cardBorder} ${themeClasses.buttonText} font-medium text-sm transition-all`}
          >
            Editar
          </button>
          <button
            onClick={() => onDelete(person)}
            disabled={isDeleting}
            className="px-4 py-2.5 bg-red-500 text-white rounded-xl font-medium text-sm transition-all flex items-center gap-2 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDeleting ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                Eliminando...
              </>
            ) : (
              <>
                <i className="ri-delete-bin-line"></i>
                Eliminar
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Form Modal ──────────────────────────────────────────────────────────────

function PersonFormModal({
  themeClasses,
  isDarkMode,
  yards,
  form,
  isEdit,
  isSubmitting,
  allowedTypes,
  onClose,
  onSubmit,
}: {
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  yards: { id: string; name: string }[];
  form: PersonFormData;
  isEdit: boolean;
  isSubmitting: boolean;
  allowedTypes: TruckYardUserType[];
  onClose: () => void;
  onSubmit: (data: PersonFormData) => void;
}) {
  const [localForm, setLocalForm] = useState<PersonFormData>(form);
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
  const [idPreview, setIdPreview] = useState<string | null>(
    form.identification_url || null,
  );

  useEffect(() => {
    if (form.identification_url && !localForm.identificationFile) {
      setIdPreview(form.identification_url);
    }
  }, [form.identification_url, localForm.identificationFile]);

  const meta = TYPE_META[localForm.type];

  const handleIdFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLocalForm((prev) => ({ ...prev, identificationFile: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setIdPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeIdFile = () => {
    setLocalForm((prev) => ({
      ...prev,
      identificationFile: null,
      identification_url: "",
    }));
    setIdPreview(null);
  };

  const surface = isDarkMode ? "bg-[#2a2a2a]" : "bg-gray-50";
  const cardCls = `w-full px-3 py-2.5 ${surface} border border-transparent rounded-xl ${themeClasses.text} focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500/50 outline-none transition-all text-sm`;
  const labelCls = `text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-1.5 block`;

  const set = (field: string, value: string | boolean) => {
    setLocalForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const setType = (type: TruckYardUserType) => {
    setLocalForm((prev) => ({
      ...prev,
      type,
      // La selección se conserva al cambiar de tipo; sólo se recorta al primero
      // si el tipo nuevo es de un solo patio, para no dejar el formulario en un
      // estado que la validación rechazaría sin decir por qué.
      yard_ids: allowsMultipleYards(type)
        ? prev.yard_ids
        : prev.yard_ids.slice(0, 1),
    }));
    setErrors((prev) => ({ ...prev, yard_ids: undefined }));
  };

  const setSchedule = (
    day: keyof WeekSchedule,
    field: keyof DaySchedule,
    value: string | boolean,
  ) => {
    setLocalForm((prev) => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [day]: { ...prev.schedule[day], [field]: value },
      },
    }));
  };

  const validate = (): boolean => {
    const e: Partial<Record<string, string>> = {};
    if (!localForm.firstName.trim()) e.firstName = "Requerido";
    if (!localForm.lastName.trim()) e.lastName = "Requerido";
    if (!localForm.username.trim()) e.username = "Requerido";
    if (!localForm.phone.trim()) e.phone = "Requerido";
    if (!localForm.email.trim()) e.email = "Requerido";
    // Mínimo un patio para los tipos recortados: dejarlo vacío era lo que
    // apagaba el recorte por rol y les hacía ver todos los patios (#497).
    if (localForm.yard_ids.length === 0 && !allowsNoYards(localForm.type)) {
      e.yard_ids = allowsMultipleYards(localForm.type)
        ? "Seleccione al menos un patio"
        : "Seleccione un patio";
    } else if (
      !allowsMultipleYards(localForm.type) &&
      localForm.yard_ids.length > 1
    ) {
      e.yard_ids = "Este tipo de personal solo puede tener un patio";
    }
    if (!isEdit) {
      if (!localForm.password) e.password = "Requerida";
      if (localForm.password !== localForm.confirmPassword)
        e.confirmPassword = "Las contraseñas no coinciden";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (ev: React.FormEvent) => {
    ev.preventDefault();
    if (validate()) onSubmit(localForm);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className={`relative ${themeClasses.cardBg} rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto`}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-5 border-b border-gray-700/30 bg-inherit rounded-t-2xl">
          <h2 className={`text-base font-bold ${themeClasses.text}`}>
            {isEdit ? `Editar ${meta.singular}` : `Nuevo ${meta.singular}`}
          </h2>
          <button
            onClick={onClose}
            className={`p-1.5 ${themeClasses.buttonHover} rounded-lg transition-colors`}
          >
            <i className={`ri-close-line text-xl ${themeClasses.text}`}></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Type selector */}
          <div>
            <p
              className={`text-[10px] uppercase tracking-widest font-semibold ${themeClasses.textSecondary} mb-4`}
            >
              Tipo de Personal
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {TYPE_ORDER.map((t) => {
                const tMeta = TYPE_META[t];
                const active = localForm.type === t;
                const disabled = isEdit || !allowedTypes.includes(t);
                return (
                  <button
                    key={t}
                    type="button"
                    disabled={disabled}
                    onClick={() => setType(t)}
                    className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border transition-all text-sm ${
                      active
                        ? `${tMeta.avatarBg} ${tMeta.avatarText} border-current`
                        : `${surface} border-gray-700/30 ${themeClasses.textSecondary}`
                    } ${
                      disabled
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:border-current"
                    }`}
                  >
                    <i className={tMeta.icon}></i>
                    <span className="font-medium">{tMeta.singular}</span>
                  </button>
                );
              })}
            </div>
            {isEdit && (
              <p className={`text-[11px] ${themeClasses.textSecondary} mt-2`}>
                El tipo no se puede cambiar al editar.
              </p>
            )}
          </div>

          {/* Header with Identification and basic info */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            <div className="lg:col-span-3">
              <p
                className={`text-[10px] uppercase tracking-widest font-semibold ${themeClasses.textSecondary} mb-4`}
              >
                Identificación
              </p>
              <p
                className={`text-xs ${themeClasses.textSecondary} mb-3 leading-relaxed`}
              >
                Suba una imagen de la identificación (INE, credencial, etc.)
              </p>
              <div
                className={`relative w-full h-[200px] ${surface} border-2 border-dashed ${
                  idPreview ? "border-blue-500/50" : "border-gray-600/50"
                } rounded-xl overflow-hidden transition-all hover:border-blue-500/70 cursor-pointer group`}
                onClick={() =>
                  document.getElementById("identification-input")?.click()
                }
              >
                <input
                  id="identification-input"
                  type="file"
                  accept="image/*"
                  onChange={handleIdFileChange}
                  className="hidden"
                />
                {idPreview ? (
                  <>
                    <img
                      src={idPreview}
                      alt="Identificación"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <span className="text-white text-sm font-medium">
                        Cambiar imagen
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeIdFile();
                      }}
                      className="absolute top-2 right-2 w-7 h-7 bg-red-500/90 hover:bg-red-500 rounded-full flex items-center justify-center transition-colors"
                    >
                      <i className="ri-delete-bin-line text-white text-sm"></i>
                    </button>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full gap-3">
                    <div
                      className={`w-16 h-16 rounded-xl ${
                        isDarkMode ? "bg-gray-700/50" : "bg-gray-200"
                      } flex items-center justify-center`}
                    >
                      <i
                        className={`ri-id-card-line text-3xl ${themeClasses.textSecondary}`}
                      ></i>
                    </div>
                    <div className="text-center">
                      <p className={`text-sm font-medium ${themeClasses.text}`}>
                        Subir identificación
                      </p>
                      <p
                        className={`text-xs ${themeClasses.textSecondary} mt-1`}
                      >
                        Haz clic o arrastra una imagen
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="lg:col-span-2">
              <p
                className={`text-[10px] uppercase tracking-widest font-semibold ${themeClasses.textSecondary} mb-4`}
              >
                Información del {meta.singular}
              </p>
              <div className="space-y-4">
                <div>
                  <label className={labelCls}>
                    Nombre <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={localForm.firstName}
                    onChange={(e) => set("firstName", e.target.value)}
                    className={`${cardCls} ${errors.firstName ? "border-red-500/60" : ""}`}
                    placeholder="Ingrese el nombre..."
                  />
                  {errors.firstName && (
                    <span className="text-red-400 text-xs mt-1">
                      {errors.firstName}
                    </span>
                  )}
                </div>
                <div>
                  <label className={labelCls}>
                    Apellido <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={localForm.lastName}
                    onChange={(e) => set("lastName", e.target.value)}
                    className={`${cardCls} ${errors.lastName ? "border-red-500/60" : ""}`}
                    placeholder="Ingrese el apellido..."
                  />
                  {errors.lastName && (
                    <span className="text-red-400 text-xs mt-1">
                      {errors.lastName}
                    </span>
                  )}
                </div>
                <div>
                  <label className={labelCls}>
                    Usuario <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={localForm.username}
                    onChange={(e) => set("username", e.target.value)}
                    className={`${cardCls} ${errors.username ? "border-red-500/60" : ""}`}
                    placeholder="Ingrese el usuario..."
                  />
                  {errors.username && (
                    <span className="text-red-400 text-xs mt-1">
                      {errors.username}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Contact and Authentication info */}
          <div>
            <p
              className={`text-[10px] uppercase tracking-widest font-semibold ${themeClasses.textSecondary} mb-4`}
            >
              Contacto y Autenticación
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>
                  Teléfono <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <div
                    className={`flex items-center gap-1.5 px-3 py-2.5 ${surface} rounded-xl flex-shrink-0`}
                  >
                    <span className="text-base">🇲🇽</span>
                    <span className={`text-sm ${themeClasses.textSecondary}`}>
                      +52
                    </span>
                  </div>
                  <input
                    type="tel"
                    value={localForm.phone}
                    onChange={(e) => set("phone", e.target.value)}
                    className={`${cardCls} flex-1 ${errors.phone ? "border-red-500/60" : ""}`}
                    placeholder="Número..."
                  />
                </div>
                {errors.phone && (
                  <span className="text-red-400 text-xs mt-1">
                    {errors.phone}
                  </span>
                )}
              </div>
              <div>
                <label className={labelCls}>
                  Correo electrónico <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={localForm.email}
                  onChange={(e) => set("email", e.target.value)}
                  className={`${cardCls} ${errors.email ? "border-red-500/60" : ""}`}
                  placeholder="Ingrese el correo..."
                />
                {errors.email && (
                  <span className="text-red-400 text-xs mt-1">
                    {errors.email}
                  </span>
                )}
              </div>
              {!isEdit && (
                <>
                  <div>
                    <label className={labelCls}>
                      Contraseña <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      value={localForm.password}
                      onChange={(e) => set("password", e.target.value)}
                      className={`${cardCls} ${errors.password ? "border-red-500/60" : ""}`}
                      placeholder="Ingrese contraseña..."
                    />
                    {errors.password && (
                      <span className="text-red-400 text-xs mt-1">
                        {errors.password}
                      </span>
                    )}
                  </div>
                  <div>
                    <label className={labelCls}>
                      Confirmar contraseña{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      value={localForm.confirmPassword}
                      onChange={(e) => set("confirmPassword", e.target.value)}
                      className={`${cardCls} ${errors.confirmPassword ? "border-red-500/60" : ""}`}
                      placeholder="Confirme la contraseña..."
                    />
                    {errors.confirmPassword && (
                      <span className="text-red-400 text-xs mt-1">
                        {errors.confirmPassword}
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Yard Assignment and Status */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <p
                className={`text-[10px] uppercase tracking-widest font-semibold ${themeClasses.textSecondary} mb-4`}
              >
                {allowsMultipleYards(localForm.type)
                  ? "Patios Asignados"
                  : "Patio Asignado"}
              </p>
              <div>
                {allowsMultipleYards(localForm.type) ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {yards.map((yard) => {
                      const checked = localForm.yard_ids.includes(yard.id);
                      return (
                        <button
                          key={yard.id}
                          type="button"
                          onClick={() => {
                            setLocalForm((prev) => ({
                              ...prev,
                              yard_ids: checked
                                ? prev.yard_ids.filter((id) => id !== yard.id)
                                : [...prev.yard_ids, yard.id],
                            }));
                            setErrors((prev) => ({
                              ...prev,
                              yard_ids: undefined,
                            }));
                          }}
                          className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm transition-all ${
                            checked
                              ? "border-blue-500 bg-blue-500/10 text-blue-400"
                              : `${surface} border-gray-700/30 ${themeClasses.textSecondary}`
                          }`}
                        >
                          <span
                            className={`w-4 h-4 rounded border flex items-center justify-center ${
                              checked
                                ? "bg-blue-500 border-blue-500"
                                : "border-gray-500"
                            }`}
                          >
                            {checked && (
                              <i className="ri-check-line text-white text-xs" />
                            )}
                          </span>
                          {yard.name}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  // Vigilante e inspector: un solo patio, pero guardado en el
                  // mismo `yard_ids` que los demás. El <select> es la regla de
                  // MULTI_YARD_TYPES hecha UI, no una limitación del esquema.
                  <select
                    value={localForm.yard_ids[0] || ""}
                    onChange={(e) => {
                      const id = e.target.value;
                      setLocalForm((prev) => ({
                        ...prev,
                        yard_ids: id ? [id] : [],
                      }));
                      setErrors((prev) => ({ ...prev, yard_ids: undefined }));
                    }}
                    className={cardCls}
                  >
                    <option value="">Seleccione un patio</option>
                    {yards.map((yard) => (
                      <option key={yard.id} value={yard.id}>
                        {yard.name}
                      </option>
                    ))}
                  </select>
                )}
                {errors.yard_ids && (
                  <p className="text-xs text-red-400 mt-1">{errors.yard_ids}</p>
                )}
              </div>
            </div>

            <div>
              <p
                className={`text-[10px] uppercase tracking-widest font-semibold ${themeClasses.textSecondary} mb-4`}
              >
                Estado
              </p>
              <div className="flex gap-3">
                {(["active", "inactive"] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => set("status", s)}
                    className={`flex-1 flex items-center gap-2 px-3 py-2.5 rounded-xl border transition-all text-sm ${
                      localForm.status === s
                        ? "border-green-500/40 bg-green-500/10 text-green-400"
                        : `${surface} border-gray-700/30 ${themeClasses.textSecondary}`
                    }`}
                  >
                    <div
                      className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center ${
                        localForm.status === s
                          ? "border-green-400"
                          : "border-gray-500"
                      }`}
                    >
                      {localForm.status === s && (
                        <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
                      )}
                    </div>
                    <span className="font-medium">
                      {s === "active" ? "Activo" : "Inactivo"}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Geofence validation — los admins (de compañía y de patios) no
              pasan por esta validación: trabajan desde una oficina. */}
          {usesGeofenceValidation(localForm.type) && (
            <div>
              <p
                className={`text-[10px] uppercase tracking-widest font-semibold ${themeClasses.textSecondary} mb-4`}
              >
                Validación por Geocerca
              </p>
              <button
                type="button"
                onClick={() =>
                  set(
                    "geofence_validation_enabled",
                    !localForm.geofence_validation_enabled,
                  )
                }
                className={`w-full flex items-center justify-between gap-4 px-4 py-3 rounded-xl border transition-all text-left ${
                  localForm.geofence_validation_enabled
                    ? "border-green-500/40 bg-green-500/10"
                    : `${surface} border-amber-500/40 bg-amber-500/10`
                }`}
              >
                <span className="min-w-0">
                  <span
                    className={`block text-sm font-medium ${themeClasses.text}`}
                  >
                    {localForm.geofence_validation_enabled
                      ? "Activa"
                      : "Desactivada"}
                  </span>
                  <span
                    className={`block text-xs mt-0.5 leading-relaxed ${themeClasses.textSecondary}`}
                  >
                    {localForm.geofence_validation_enabled
                      ? "Se cierra la sesión si la persona sale del perímetro del patio."
                      : "No se verifica la ubicación. El horario de trabajo se sigue validando."}
                  </span>
                </span>
                <span
                  className={`shrink-0 w-11 h-6 rounded-full p-0.5 transition-colors ${
                    localForm.geofence_validation_enabled
                      ? "bg-green-500"
                      : "bg-gray-500"
                  }`}
                >
                  <span
                    className={`block w-5 h-5 rounded-full bg-white transition-transform ${
                      localForm.geofence_validation_enabled
                        ? "translate-x-5"
                        : "translate-x-0"
                    }`}
                  />
                </span>
              </button>
            </div>
          )}

          {/* Schedule */}
          <div>
            <p
              className={`text-[10px] uppercase tracking-widest font-semibold ${themeClasses.textSecondary} mb-4`}
            >
              Horario de Trabajo
            </p>
            <p
              className={`text-xs ${themeClasses.textSecondary} mb-3 leading-relaxed`}
            >
              Asigne el horario marcando los días correspondientes. Si no desea
              asignar un día, deje la casilla desmarcada.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-2">
              {DAYS.map((d) => {
                const day = localForm.schedule[d.key];
                return (
                  <div key={d.key} className="flex flex-col items-center gap-1">
                    <span
                      className={`text-[10px] font-semibold ${themeClasses.textSecondary}`}
                    >
                      {d.label.slice(0, 3)}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setSchedule(d.key, "enabled", !day?.enabled)
                      }
                      className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                        day?.enabled
                          ? "bg-green-500 border-green-500"
                          : `border-gray-500 ${surface}`
                      }`}
                    >
                      {day?.enabled && (
                        <i className="ri-check-line text-white text-xs"></i>
                      )}
                    </button>
                    {day?.enabled && (
                      <>
                        <select
                          value={day?.from || ""}
                          onChange={(e) =>
                            setSchedule(d.key, "from", e.target.value)
                          }
                          className={`w-full text-[10px] ${surface} border border-gray-700/30 rounded-lg px-1 py-1 ${themeClasses.text} outline-none`}
                        >
                          {HOURS.map((h) => (
                            <option key={h} value={h}>
                              {h}
                            </option>
                          ))}
                        </select>
                        <select
                          value={day?.to || ""}
                          onChange={(e) =>
                            setSchedule(d.key, "to", e.target.value)
                          }
                          className={`w-full text-[10px] ${surface} border border-gray-700/30 rounded-lg px-1 py-1 ${themeClasses.text} outline-none`}
                        >
                          {HOURS.map((h) => (
                            <option key={h} value={h}>
                              {h}
                            </option>
                          ))}
                        </select>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div
            className={`flex justify-end gap-3 pt-4 border-t ${themeClasses.cardBorder}`}
          >
            <button
              type="button"
              onClick={onClose}
              className={`px-5 py-2.5 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-xl ${themeClasses.buttonText} font-medium text-sm transition-colors`}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-5 py-2.5 rounded-xl font-medium text-sm transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: brand.primary, color: brand.accent }}
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  {isEdit ? "Actualizando..." : "Creando..."}
                </>
              ) : isEdit ? (
                `Actualizar ${meta.singular}`
              ) : (
                `Crear ${meta.singular}`
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Pagination ───────────────────────────────────────────────────────────────

function PersonalPagination({
  currentPage,
  totalPages,
  onPageChange,
}: {
  currentPage: number;
  totalPages: number;
  onPageChange: (p: number) => void;
}) {
  if (totalPages <= 1) return null;

  return (
    <PaginationControls
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={onPageChange}
    />
  );
}

// ─── Empty State ──────────────────────────────────────────────────────────────

function EmptyState({
  themeClasses,
  onAdd,
}: {
  themeClasses: ThemeClasses;
  onAdd: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-16">
      <div
        className={`w-20 h-20 rounded-full flex items-center justify-center mb-4 ${themeClasses.cardBg}`}
      >
        <i
          className={`ri-team-line text-4xl ${themeClasses.textSecondary}`}
        ></i>
      </div>
      <h3 className={`text-lg font-medium ${themeClasses.text} mb-2`}>
        Sin personal registrado
      </h3>
      <p className={`text-sm ${themeClasses.textSecondary} mb-4`}>
        No se encontraron registros con los filtros actuales.
      </p>
      <button
        onClick={onAdd}
        className="px-4 py-2 rounded-lg font-medium text-sm"
        style={{ backgroundColor: brand.primary, color: brand.accent }}
      >
        <i className="ri-add-line mr-1"></i>
        Agregar Personal
      </button>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function PersonalSection() {
  const { isDarkMode } = useDarkMode();
  const {
    yards,
    guards,
    supervisors,
    inspectors,
    admins,
    yardAdmins,
    usersLoading,
    fetchYards,
    fetchYardUsers,
    createYardUser,
    updateYardUser,
    createYardUserWithFile,
    updateYardUserWithFile,
    deleteYardUser,
    resetUsers,
  } = useTruckYardStore();

  const {
    getAssignedYardIds,
    hasYardRestrictions,
    hasNoAssignedYards,
    isAdmin,
    isSupervisor,
    isGuard,
    isYardAdmin,
  } = useTruckYardRole();
  const assignedYardIds = getAssignedYardIds();

  // Supervisors and guards (truck-yard roles) only manage guards.
  //
  // El admin de COMPAÑÍA suma `yardAdmins` (#561): es quien le asigna patios al
  // admin de patios, y sin esa pestaña no hay ninguna forma en el producto de
  // poblarle `yard_ids` — que es justo el campo del que depende todo lo que ve.
  //
  // El admin de PATIOS gestiona lo mismo que el de compañía menos los dos tipos
  // de admin: no puede crear pares suyos ni tocar a quien está por encima. La
  // lista que ve además queda recortada a sus patios por `filtered`, más abajo.
  const allowedTypes: TruckYardUserType[] = useMemo(() => {
    if (isSupervisor || isGuard) return ["guards"];
    if (isYardAdmin) return ["guards", "supervisors", "inspectors"];
    if (isAdmin) return ["guards", "supervisors", "inspectors", "yardAdmins"];
    return ["guards", "supervisors", "inspectors", "yardAdmins", "admins"];
  }, [isAdmin, isGuard, isSupervisor, isYardAdmin]);

  const themeClasses: ThemeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-white",
    cardBorder: isDarkMode ? "border-dark-border" : "border-gray-200",
    buttonHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-100",
    buttonText: isDarkMode ? "text-gray-400" : "text-gray-600",
    buttonTextHover: isDarkMode ? "hover:text-white" : "hover:text-gray-900",
  };

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  // Filtro por patios, selección múltiple: [] = todos (los del alcance),
  // NO_YARD_FILTER suma al personal sin patio. Una persona pasa si tiene
  // CUALQUIERA de los patios elegidos en su `yard_ids`.
  const [selectedYardsPick, setSelectedYardsPick] = useState<string[]>([]);
  const [selectedType, setSelectedType] = useState<"all" | TruckYardUserType>(
    "all",
  );
  const [selectedView, setSelectedView] = useState<"grid" | "list">("grid");
  const [currentPage, setCurrentPage] = useState(1);

  const [detailsPerson, setDetailsPerson] = useState<Person | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editPerson, setEditPerson] = useState<Person | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchYards();
    allowedTypes.forEach((type) => {
      resetUsers(type);
      fetchYardUsers(type);
    });
  }, [fetchYards, fetchYardUsers, resetUsers, allowedTypes]);

  const people = useMemo<Person[]>(() => {
    const list: Person[] = [];
    if (allowedTypes.includes("guards"))
      list.push(...guards.map((u) => normalizeUser(u, "guards")));
    if (allowedTypes.includes("supervisors"))
      list.push(...supervisors.map((u) => normalizeUser(u, "supervisors")));
    if (allowedTypes.includes("inspectors"))
      list.push(...inspectors.map((u) => normalizeUser(u, "inspectors")));
    if (allowedTypes.includes("yardAdmins"))
      list.push(...yardAdmins.map((u) => normalizeUser(u, "yardAdmins")));
    if (allowedTypes.includes("admins"))
      list.push(...admins.map((u) => normalizeUser(u, "admins")));
    return list;
  }, [guards, supervisors, inspectors, admins, yardAdmins, allowedTypes]);

  // Sin el `&& assignedYardIds.length > 0` que había acá: una lista vacía en un
  // rol restringido significa "sin patios asignados", no "sin restricción"
  // (issue #497). Antes apagaba el recorte y mostraba TODOS los patios.
  const filteredYards = useMemo(() => {
    if (hasYardRestrictions()) {
      return yards.filter((y) => assignedYardIds.includes(y.id));
    }
    return yards;
  }, [yards, assignedYardIds, hasYardRestrictions]);

  // La selección se recorta contra los patios que se ofrecen: si el catálogo
  // cambia (un patio borrado, o el alcance del usuario se achica) un id que ya
  // no está en el combo no puede seguir filtrando sin un chip que lo muestre.
  const selectedYards = useMemo(
    () =>
      pruneYardSelection(
        selectedYardsPick,
        filteredYards.map((y) => y.id),
      ),
    [selectedYardsPick, filteredYards],
  );

  // Sin entrada "Todos": en un multi, "todos" es no elegir nada. "Sin patio"
  // existe porque hay personal legítimamente sin patio (los administradores
  // de empresa no llevan asignación).
  const yardFilterOptions = useMemo(
    () => [
      ...filteredYards.map((y) => ({ value: y.id, label: y.name })),
      { value: NO_YARD_FILTER, label: "Sin patio asignado" },
    ],
    [filteredYards],
  );

  const filtered = useMemo(() => {
    return people.filter((p) => {
      if (selectedType !== "all" && p.type !== selectedType) return false;

      // p.yard_ids ya viene normalizado por normalizeUser (cubre el escalar
      // `yard_id` de los documentos sin migrar).
      if (!matchesYardSelection(p.yard_ids, selectedYards)) return false;

      if (hasYardRestrictions()) {
        // p.yard_ids ya viene normalizado por normalizeUser, que cubre el
        // documento sin migrar. Sin patios, la persona no pasa el filtro.
        const ok = p.yard_ids.some((id) => assignedYardIds.includes(id));
        if (!ok) return false;
      }

      const q = searchQuery.toLowerCase();
      const matchSearch =
        !q ||
        `${p.firstName} ${p.lastName}`.toLowerCase().includes(q) ||
        p.email.toLowerCase().includes(q) ||
        p.id.toLowerCase().includes(q);
      const matchStatus = !selectedStatus || p.status === selectedStatus;
      return matchSearch && matchStatus;
    });
  }, [
    people,
    selectedType,
    searchQuery,
    selectedStatus,
    selectedYards,
    assignedYardIds,
    hasYardRestrictions,
  ]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  const typeCounts = useMemo(() => {
    return people.reduce(
      (acc, p) => {
        acc[p.type] = (acc[p.type] || 0) + 1;
        return acc;
      },
      {
        guards: 0,
        supervisors: 0,
        inspectors: 0,
        admins: 0,
        yardAdmins: 0,
      } as Record<TruckYardUserType, number>,
    );
  }, [people]);

  const handleOpenAdd = () => {
    setEditPerson(null);
    setFormOpen(true);
  };

  const handleOpenEdit = (person: Person) => {
    setDetailsPerson(null);
    setEditPerson(person);
    setFormOpen(true);
  };

  const handleSubmit = async (data: PersonFormData) => {
    setIsSubmitting(true);
    const type = editPerson ? editPerson.type : data.type;
    const meta = TYPE_META[type];

    try {
      const hasFile = !!data.identificationFile;

      if (hasFile) {
        const formData = new FormData();
        formData.append("firstName", data.firstName);
        formData.append("lastName", data.lastName);
        formData.append("username", data.username);
        formData.append("phone", data.phone);
        formData.append("email", data.email);
        formData.append("status", data.status);
        formData.append("schedule", JSON.stringify(data.schedule));
        formData.append(
          "geofence_validation_enabled",
          String(data.geofence_validation_enabled),
        );
        if (data.password) formData.append("password", data.password);
        // Un solo campo para los cuatro tipos (#497). Va serializado porque el
        // multipart no transporta arrays.
        formData.append("yard_ids", JSON.stringify(data.yard_ids));
        if (data.identificationFile) {
          formData.append("identification", data.identificationFile);
        }

        if (editPerson) {
          await updateYardUserWithFile(type, editPerson.id, formData);
        } else {
          await createYardUserWithFile(type, formData);
        }
      } else {
        const payload: any = {
          firstName: data.firstName,
          lastName: data.lastName,
          username: data.username,
          phone: data.phone,
          email: data.email,
          status: data.status,
          schedule: data.schedule,
          geofence_validation_enabled: data.geofence_validation_enabled,
        };
        if (data.password) payload.password = data.password;
        payload.yard_ids = data.yard_ids;
        if (editPerson) {
          payload.identification_url = data.identification_url || "";
        } else if (data.identification_url) {
          payload.identification_url = data.identification_url;
        }

        if (editPerson) {
          await updateYardUser(type, editPerson.id, payload);
        } else {
          await createYardUser(type, payload);
        }
      }

      Swal.fire({
        icon: "success",
        title: editPerson
          ? `${meta.singular} actualizado`
          : `${meta.singular} creado`,
        confirmButtonColor: brand.primary,
      });

      setFormOpen(false);
      setEditPerson(null);
      await fetchYardUsers(type);
    } catch (error: any) {
      console.error("❌ Error saving user:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Hubo un error al guardar";
      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMessage,
        confirmButtonColor: "#ef4444",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (person: Person) => {
    const meta = TYPE_META[person.type];
    const result = await Swal.fire({
      title: "¿Estás seguro?",
      text: `¿Eliminar a "${person.firstName} ${person.lastName}" (${meta.singular})? Esta acción no se puede deshacer.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: brand.primary,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });
    if (result.isConfirmed) {
      setIsDeleting(true);
      try {
        await deleteYardUser(person.type, person.id);
        setDetailsPerson(null);
        await fetchYardUsers(person.type);
        Swal.fire({
          title: "Eliminado",
          icon: "success",
          confirmButtonColor: brand.primary,
        });
      } catch {
        Swal.fire({
          title: "Error",
          text: "No se pudo eliminar",
          icon: "error",
          confirmButtonColor: "#ef4444",
        });
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const initialForm: PersonFormData = useMemo(() => {
    if (editPerson) {
      return {
        type: editPerson.type,
        firstName: editPerson.firstName,
        lastName: editPerson.lastName,
        username: editPerson.username,
        phone: editPerson.phone,
        email: editPerson.email,
        password: "",
        confirmPassword: "",
        status: editPerson.status,
        yard_ids: editPerson.yard_ids || [],
        schedule: editPerson.schedule,
        geofence_validation_enabled:
          editPerson.geofence_validation_enabled !== false,
        identification_url: editPerson.identification_url || "",
        identificationFile: null,
      };
    }
    const seedType: TruckYardUserType =
      selectedType !== "all" && allowedTypes.includes(selectedType)
        ? (selectedType as TruckYardUserType)
        : allowedTypes[0];
    return { ...EMPTY_FORM, type: seedType };
  }, [editPerson, selectedType, allowedTypes]);

  return (
    <div className="h-full flex flex-col relative">
      <HeaderComponent
        themeClasses={themeClasses}
        isDarkMode={isDarkMode}
        title="Personal"
        subtitle="Administra vigilantes, supervisores, inspectores y administradores"
        searchQuery={searchQuery}
        selectedStatus={selectedStatus}
        selectedView={selectedView}
        onSearchChange={(v: string) => {
          setSearchQuery(v);
          setCurrentPage(1);
        }}
        onStatusChange={(v: string) => {
          setSelectedStatus(v);
          setCurrentPage(1);
        }}
        onViewChange={setSelectedView}
        onAdd={handleOpenAdd}
        addButtonText="Agregar Personal"
        addButtonShortText="Agregar"
        searchPlaceholder="Buscar personal..."
        extraFilters={[
          {
            multi: true,
            values: selectedYards,
            onChange: (v: string[]) => {
              setSelectedYardsPick(v);
              setCurrentPage(1);
            },
            options: yardFilterOptions,
            placeholder: "Todos los patios",
            noOptionsMessage: "Sin patios disponibles",
          },
        ]}
      />

      {hasNoAssignedYards() && (
        <div className="px-4 sm:px-6 pt-4">
          <NoAssignedYardsNotice isDarkMode={isDarkMode} what="el personal" />
        </div>
      )}

      {/* Type filter chips */}
      {allowedTypes.length > 1 && (
        <div className="px-4 sm:px-6 pt-4 flex flex-wrap gap-2">
          {(
            [
              { key: "all" as const, label: "Todos", count: people.length },
              ...allowedTypes.map((t) => ({
                key: t,
                label: TYPE_META[t].plural,
                count: typeCounts[t],
              })),
            ] as {
              key: "all" | TruckYardUserType;
              label: string;
              count: number;
            }[]
          ).map((chip) => {
            const active = selectedType === chip.key;
            const meta = chip.key === "all" ? null : TYPE_META[chip.key];
            return (
              <button
                key={chip.key}
                onClick={() => {
                  setSelectedType(chip.key);
                  setCurrentPage(1);
                }}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium transition-all ${
                  active
                    ? meta
                      ? `${meta.avatarBg} ${meta.avatarText} border-current`
                      : "bg-primary-500/15 text-primary-400 border-current"
                    : `${themeClasses.cardBg} ${themeClasses.cardBorder} ${themeClasses.textSecondary} hover:${themeClasses.text}`
                }`}
              >
                {meta && <i className={meta.icon}></i>}
                <span>{chip.label}</span>
                <span
                  className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                    active ? "bg-white/10" : "bg-gray-500/15"
                  }`}
                >
                  {chip.count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        {usersLoading && people.length === 0 ? (
          <div
            className={`py-16 text-center text-sm ${themeClasses.textSecondary}`}
          >
            Cargando personal...
          </div>
        ) : paginated.length === 0 ? (
          <EmptyState themeClasses={themeClasses} onAdd={handleOpenAdd} />
        ) : selectedView === "grid" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {paginated.map((p) => (
              <PersonGridCard
                key={`${p.type}:${p.id}`}
                person={p}
                themeClasses={themeClasses}
                isDarkMode={isDarkMode}
                onDetails={setDetailsPerson}
                onEdit={handleOpenEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {paginated.map((p) => (
              <PersonListRow
                key={`${p.type}:${p.id}`}
                person={p}
                themeClasses={themeClasses}
                onDetails={setDetailsPerson}
                onEdit={handleOpenEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}

        <PersonalPagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>

      {detailsPerson && (
        <PersonDetailsModal
          person={detailsPerson}
          yards={filteredYards.map((y) => ({ id: y.id, name: y.name }))}
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          isDeleting={isDeleting}
          onClose={() => setDetailsPerson(null)}
          onEdit={handleOpenEdit}
          onDelete={handleDelete}
        />
      )}

      {formOpen && (
        <PersonFormModal
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          isSubmitting={isSubmitting}
          yards={filteredYards.map((y) => ({ id: y.id, name: y.name }))}
          form={initialForm}
          isEdit={!!editPerson}
          allowedTypes={allowedTypes}
          onClose={() => {
            setFormOpen(false);
            setEditPerson(null);
          }}
          onSubmit={handleSubmit}
        />
      )}
    </div>
  );
}
